import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VerifiedIcon from '@mui/icons-material/VerifiedUser';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { headerBranchId } from './helpers';
import { branchService } from '@/data/branchService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  EXTERNAL_INTEGRATION_STATUS_LABELS,
  ExternalIntegrationStatus,
  FoodIntegration,
  FOOD_PROVIDER_LABELS
} from '@/types/FoodDelivery.interface';
import { Branch } from '@/types/Branch.interface';
import IntegrationFormDialog from './components/IntegrationFormDialog';
import RemoteStorePickerDialog from './components/RemoteStorePickerDialog';
import IntegrationErrorNote, {
  ProviderErrorAlert
} from './components/IntegrationErrorNote';

const STATUS_COLOR: Record<
  ExternalIntegrationStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  [ExternalIntegrationStatus.PENDING]: 'info',
  [ExternalIntegrationStatus.ACTIVE]: 'success',
  [ExternalIntegrationStatus.PAUSED]: 'warning',
  [ExternalIntegrationStatus.DISABLED]: 'default',
  [ExternalIntegrationStatus.ERROR]: 'error'
};

const FoodDeliveryIntegration: React.FC = () => {
  const { isBranchAdmin, isAdminView, currentBranch } = useUserViewMode();

  const [branches, setBranches] = useState<Branch[]>([]);
  const headerBranch = headerBranchId(currentBranch, isAdminView);
  const [branchId, setBranchId] = useState<number | null>(headerBranch);
  // Üst menüden şube değişince sayfa da o şubeye geçer.
  useEffect(() => {
    if (headerBranch) setBranchId(headerBranch);
  }, [headerBranch]);
  const [rows, setRows] = useState<FoodIntegration[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  // Mağaza seçici (storeId'yi elle aramak yerine listeden seç).
  const [storePickerId, setStorePickerId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FoodIntegration | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);
  /**
   * Sağlayıcı hataları toast'ta gösterilmez: uzundurlar, "talep no" içerirler ve
   * 5 saniyede kaybolan bir kutuda okunup kopyalanamazlar. Sayfada kalıcı durur.
   */
  const [providerError, setProviderError] = useState<string | null>(null);

  // Admin/şirket görünümünde şube listesini getir.
  useEffect(() => {
    if (isBranchAdmin) return;
    branchService
      .getAllFlat({ getAll: true })
      .then((list) => setBranches(list))
      .catch(() =>
        setToast({ msg: 'Şubeler getirilemedi.', severity: 'error' })
      );
  }, [isBranchAdmin]);

  const load = useCallback(async () => {
    if (!branchId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      setRows(await foodDeliveryService.listByBranch(branchId));
    } catch {
      setToast({ msg: 'Entegrasyonlar getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleValidate = async (row: FoodIntegration) => {
    setBusyId(row.id);
    setProviderError(null);
    try {
      const res = await foodDeliveryService.validate(row.id);
      if (res.ok) {
        setToast({ msg: 'Credential doğrulandı.', severity: 'success' });
      } else {
        setProviderError(
          `Doğrulama başarısız: ${res.message ?? 'bilinmeyen hata'}`
        );
      }
      await load();
    } catch {
      setProviderError('Doğrulama isteği başarısız (sunucuya ulaşılamadı).');
    } finally {
      setBusyId(null);
    }
  };

  const handleHealth = async (row: FoodIntegration) => {
    setBusyId(row.id);
    setProviderError(null);
    try {
      const res = await foodDeliveryService.health(row.id);
      if (res.ok) {
        setToast({
          msg: `Sağlıklı${res.detail ? ` (${res.detail})` : ''}`,
          severity: 'success'
        });
      } else {
        setProviderError(`Sağlıksız: ${res.detail ?? 'erişilemiyor'}`);
      }
    } catch {
      setProviderError('Sağlık kontrolü başarısız (sunucuya ulaşılamadı).');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: FoodIntegration) => {
    if (
      !window.confirm(
        `"${FOOD_PROVIDER_LABELS[row.providerCode]}" entegrasyonu kaldırılsın mı?`
      )
    )
      return;
    setBusyId(row.id);
    try {
      await foodDeliveryService.remove(row.id);
      setToast({ msg: 'Entegrasyon kaldırıldı.', severity: 'info' });
      await load();
    } catch {
      setToast({ msg: 'Kaldırma başarısız.', severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: FoodIntegration) => {
    setEditing(row);
    setDialogOpen(true);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              Yemek Platformu Entegrasyonları
            </Typography>
            <Typography variant="subtitle2">
              Yemeksepeti, Trendyol GO ve Getir entegrasyonlarını yönetin.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!branchId}
          >
            Yeni Entegrasyon
          </Button>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card>
          <CardContent>
            {!isBranchAdmin && (
              <Autocomplete
                sx={{ maxWidth: 420, mb: 3 }}
                options={branches}
                getOptionLabel={(b) => b.name}
                value={branches.find((b) => b.id === branchId) ?? null}
                onChange={(_e, val) => setBranchId(val?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} label="Şube seç" />
                )}
              />
            )}

            {providerError && (
              <ProviderErrorAlert
                message={providerError}
                onClose={() => setProviderError(null)}
              />
            )}

            {!branchId ? (
              <Alert severity="info">
                Entegrasyonları görüntülemek için bir şube seçin.
              </Alert>
            ) : loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Alert severity="info">
                Bu şube için entegrasyon yok. Sağ üstteki{' '}
                <strong>Yeni Entegrasyon</strong> düğmesiyle sihirbazı başlatın.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Platform</TableCell>
                      <TableCell>Görünen Ad</TableCell>
                      <TableCell>Vendor ID</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Credential</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>
                            {FOOD_PROVIDER_LABELS[row.providerCode]}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.displayName ?? '—'}</TableCell>
                        <TableCell>{row.externalVendorId ?? '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              EXTERNAL_INTEGRATION_STATUS_LABELS[row.status]
                            }
                            color={STATUS_COLOR[row.status]}
                          />
                          {/* Hata notu ERROR'a düşmeden de görünür: 5 ardışık
                              hatanın 2.'sinde de operatör durumu görsün. */}
                          <IntegrationErrorNote
                            message={row.lastErrorMessage}
                            at={row.lastErrorAt}
                            pollFailures={row.consecutivePollFailures ?? 0}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Chip
                              size="small"
                              variant="outlined"
                              color={row.hasCredentials ? 'success' : 'default'}
                              label={row.hasCredentials ? 'Var' : 'Yok'}
                            />
                            {row.hasCredentials &&
                              !row.credentialKeys.includes('storeId') && (
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  icon={<StorefrontIcon fontSize="small" />}
                                  label="Mağaza seçilmedi"
                                />
                              )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                          >
                            {busyId === row.id ? (
                              <CircularProgress size={20} sx={{ m: 1 }} />
                            ) : (
                              <>
                                <Tooltip title="Credential doğrula">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleValidate(row)}
                                      disabled={!row.hasCredentials}
                                    >
                                      <VerifiedIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Mağazaları getir / değiştir">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color={
                                        row.credentialKeys.includes('storeId')
                                          ? 'default'
                                          : 'warning'
                                      }
                                      onClick={() => setStorePickerId(row.id)}
                                      disabled={!row.hasCredentials}
                                    >
                                      <StorefrontIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Sağlık kontrolü">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleHealth(row)}
                                  >
                                    <MonitorHeartIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Düzenle">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEdit(row)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Kaldır">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(row)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <RemoteStorePickerDialog
        integrationId={storePickerId}
        open={storePickerId != null}
        onClose={() => setStorePickerId(null)}
        onSelected={(store) => {
          setToast({
            msg: `Mağaza seçildi: ${store.name} (ID: ${store.id})`,
            severity: 'success'
          });
          void load();
        }}
      />

      {branchId && (
        <IntegrationFormDialog
          open={dialogOpen}
          branchId={branchId}
          editing={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={load}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryIntegration;
